import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAssetUrl } from '../../utils/helpers';
import { apiPost } from '../../services/api';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import '../../contact-us-style.css';

/* ─── Inline SVG Icons ─────────────────────────────────────────────────────── */
const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 26, height: 26 }}>
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
  </svg>
);

const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 26, height: 26 }}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconMapPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 26, height: 26 }}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IconWhatsApp = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 26, height: 26 }}>
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.731-1.456L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.859-4.407 9.862-9.823.002-2.623-1.017-5.09-2.871-6.944-1.853-1.854-4.321-2.87-6.944-2.872-5.442 0-9.863 4.41-9.865 9.827-.001 1.771.468 3.498 1.36 5.03l-.993 3.624 3.731-.977c-.105-.045-.2-.09-.285-.118zm10.741-7.234c-.29-.145-1.716-.848-1.982-.944-.266-.096-.46-.145-.653.145-.194.291-.75.944-.919 1.138-.169.194-.339.218-.629.073-.29-.145-1.226-.452-2.335-1.441-.864-.771-1.447-1.724-1.616-2.015-.17-.29-.018-.447.127-.591.131-.13.29-.339.436-.509.145-.17.194-.291.29-.485.097-.194.049-.364-.025-.509-.073-.145-.653-1.575-.895-2.156-.236-.569-.475-.491-.653-.5H6.99c-.194 0-.509.073-.775.364-.266.291-1.017.994-1.017 2.424s1.041 2.812 1.186 3.006c.145.194 2.049 3.129 4.965 4.385.694.299 1.236.478 1.658.613.697.221 1.332.19 1.833.115.559-.084 1.716-.703 1.958-1.382.242-.679.242-1.261.169-1.382-.072-.12-.266-.193-.556-.339z" />
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 22, height: 22 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ─── Main Component ─────────────────────────────────────────────────────────── */
const ContactUs = () => {

  /* ── Form State & Inline Validation ────────────────────────────────────────── */
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    interest: 'General Enquiry',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // 'validation_passed' | null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Validate form fields
  const validateField = (name, value) => {
    let error = '';
    if (name === 'fullName') {
      if (!value.trim()) {
        error = 'Full Name is required.';
      } else if (value.trim().length < 2) {
        error = 'Full Name must be at least 2 characters.';
      }
    }
    if (name === 'email') {
      if (!value.trim()) {
        error = 'Email Address is required.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        error = 'Please enter a valid email address.';
      }
    }
    if (name === 'phone') {
      if (value.trim() && !/^[0-9+\s\-()]{7,20}$/.test(value.trim())) {
        error = 'Please enter a valid phone number (digits and optional + space -).';
      }
    }
    if (name === 'message') {
      if (!value.trim()) {
        error = 'Message is required.';
      } else if (value.trim().length < 10) {
        error = 'Message must be at least 10 characters.';
      }
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const err = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: err }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: err }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const err = validateField(key, formData[key]);
      if (err) newErrors[key] = err;
    });

    setTouched({
      fullName: true,
      email: true,
      phone: true,
      interest: true,
      message: true
    });
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      setApiError(null);
      try {
        await apiPost('/save-interested-lead.php', {
          name: formData.fullName,
          email: formData.email,
          mobile: formData.phone || 'N/A',
          interest: formData.interest,
          message: formData.message,
          source_page: '/contact-us'
        });
        setSubmitStatus('validation_passed');
      } catch (err) {
        if (err.statusCode === 409) {
          setSubmitStatus('validation_passed');
        } else {
          setApiError(err.message || 'Unable to submit enquiry. Please use direct contact options below.');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  /* ── SEO Metadata & Structured Data ────────────────────────────────────────── */
  useEffect(() => {
    document.title = "Contact PranaFlow Academy | Yoga Academy in Rishikesh";

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content',
      "Contact PranaFlow Academy in Rishikesh, India for questions about yoga teacher training, wellness courses, batches, fees, certification, demo classes, enrollment and student support."
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/contact-us");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "Contact PranaFlow Academy | Yoga Academy in Rishikesh" },
      { property: 'og:description', content: "Contact PranaFlow Academy in Rishikesh for questions about yoga courses, batches, enrollment, certification, demo classes and student support." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/contact-us" },
      { property: 'og:image', content: "https://pranaflowacademy.com/assets/rishikesh_hero.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];
    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Card Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "Contact PranaFlow Academy | Yoga Academy in Rishikesh" },
      { name: 'twitter:description', content: "Contact PranaFlow Academy for yoga course, enrollment, certification, batch and student-support enquiries." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/assets/rishikesh_hero.webp" }
    ];
    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Dynamic JSON-LD Schema Injection
    const scriptId = 'jsonld-schema-contact-us';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    const contactPageSchema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": "https://pranaflowacademy.com/contact-us#webpage",
      "url": "https://pranaflowacademy.com/contact-us",
      "name": "Contact PranaFlow Academy | Yoga Academy in Rishikesh",
      "description": "Contact PranaFlow Academy in Rishikesh, India for questions about yoga teacher training, wellness courses, batches, fees, certification, demo classes, enrollment and student support.",
      "isPartOf": {
        "@id": "https://pranaflowacademy.com/#website"
      },
      "about": {
        "@id": "https://pranaflowacademy.com/#organization"
      }
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Contact Us",
          "item": "https://pranaflowacademy.com/contact-us"
        }
      ]
    };

    script.textContent = JSON.stringify([contactPageSchema, breadcrumbSchema]);

    window.scrollTo(0, 0);

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="public-site" style={{ backgroundColor: 'var(--bg-base)' }}>
      <PublicHeader />

      {/* ──────────────────────────────────────────────────────────────────────
           SECTION 1 — HERO
          ────────────────────────────────────────────────────────────────────── */}
      <section className="cu-hero" aria-label="Contact PranaFlow Academy">
        <div className="container cu-hero-grid">
          {/* Left Column */}
          <div className="cu-hero-content">
            <span className="section-meta" style={{ marginBottom: '16px', display: 'inline-block' }}>
              CONTACT PRANAFLOW ACADEMY
            </span>
            <h1 className="cu-hero-h1">
              We're Here to Help You Choose the Right Learning Path
            </h1>
            <p className="cu-hero-intro">
              Have a question about our Yoga Teacher Training, specialized courses, batches, fees,
              certification, demo classes, enrollment, or student support? Connect with the PranaFlow
              Academy team and we'll help you find the information you need.
            </p>
            <p className="cu-hero-sub">
              PranaFlow Academy is based in Rishikesh, Uttarakhand, India, and supports students
              interested in our yoga teacher training and wellness education programs.
            </p>
            <div className="cu-hero-btns">
              <a href="mailto:pranaflowacademy@gmail.com" className="btn btn-primary">
                Email Our Team
              </a>
              <a
                href="https://wa.me/919548354084"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <IconWhatsApp />
                <span>Chat on WhatsApp</span>
              </a>
            </div>
          </div>

          {/* Right Column */}
          <div className="cu-hero-media">
            <div className="cu-hero-img-wrap">
              <img
                src={getAssetUrl('assets/rishikesh_hero.webp')}
                alt="PranaFlow Academy in Rishikesh India"
                className="cu-hero-img"
                width="640"
                height="440"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────
           SECTION 2 — CONTACT INFORMATION CARDS
          ────────────────────────────────────────────────────────────────────── */}
      <section className="cu-info section-padding" style={{ backgroundColor: 'var(--bg-surface-tint)' }}>
        <div className="container">
          <div className="cu-section-header">
            <span className="section-meta">GET IN TOUCH</span>
            <h2 className="section-title cu-section-title">
              Choose the Most Convenient Way to Reach Us
            </h2>
          </div>

          <div className="cu-cards-grid">
            {/* Card 1: Phone */}
            <div className="cu-card">
              <div className="cu-card-icon">
                <IconPhone />
              </div>
              <h3 className="cu-card-title">Call Us</h3>
              <a href="tel:09548354084" className="cu-card-link">
                09548354084
              </a>
              <p className="cu-card-sublink">+91 95483 54084</p>
              <p className="cu-card-desc">
                For questions about courses, batches, enrollment, fees, or general student guidance.
              </p>
            </div>

            {/* Card 2: Email */}
            <div className="cu-card">
              <div className="cu-card-icon">
                <IconMail />
              </div>
              <h3 className="cu-card-title">Email Us</h3>
              <a href="mailto:pranaflowacademy@gmail.com" className="cu-card-link" style={{ wordBreak: 'break-all' }}>
                pranaflowacademy@gmail.com
              </a>
              <p className="cu-card-sublink">Direct Admissions &amp; Support</p>
              <p className="cu-card-desc">
                Send us your course, enrollment, certification, or general academy questions.
              </p>
            </div>

            {/* Card 3: WhatsApp */}
            <div className="cu-card">
              <div className="cu-card-icon cu-icon-whatsapp">
                <IconWhatsApp />
              </div>
              <h3 className="cu-card-title">WhatsApp Us</h3>
              <a
                href="https://wa.me/919548354084"
                target="_blank"
                rel="noopener noreferrer"
                className="cu-card-link"
              >
                +91 95483 54084
              </a>
              <p className="cu-card-sublink">Quick Message Support</p>
              <p className="cu-card-desc">
                Quick chat support on WhatsApp for course details, batch schedules, and admission queries.
              </p>
            </div>

            {/* Card 4: Location */}
            <div className="cu-card">
              <div className="cu-card-icon">
                <IconMapPin />
              </div>
              <h3 className="cu-card-title">Rishikesh, India</h3>
              <p className="cu-card-link-text">
                Rishikesh, Uttarakhand 249201, India
              </p>
              <p className="cu-card-sublink">Yoga Capital of the World</p>
              <p className="cu-card-desc">
                PranaFlow Academy is rooted in Rishikesh, one of India's most recognized destinations for yoga learning and practice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────
           SECTION 3 — CONTACT FORM & MAIN CONTACT AREA
          ────────────────────────────────────────────────────────────────────── */}
      <section className="cu-form-section section-padding">
        <div className="container">
          <div className="cu-form-layout">

            {/* Form Column */}
            <div className="cu-form-container">
              <span className="section-meta">SEND A MESSAGE</span>
              <h2 className="section-title cu-section-title" style={{ textAlign: 'left', marginBottom: '24px' }}>
                Tell Us How We Can Help
              </h2>

              {submitStatus === 'validation_passed' ? (
                <div className="cu-validation-notice">
                  <div className="cu-notice-icon">
                    <IconCheck />
                  </div>
                  <div>
                    <h3 className="cu-notice-title">Enquiry Details Validated</h3>
                    <p className="cu-notice-body">
                      Thank you, <strong>{formData.fullName}</strong>! Your message details have been validated.
                    </p>
                    <p className="cu-notice-sub">
                      To ensure your request is answered immediately, please connect directly with our admissions team via email or WhatsApp:
                    </p>
                    <div className="cu-notice-actions">
                      <a href={`mailto:pranaflowacademy@gmail.com?subject=Enquiry regarding ${encodeURIComponent(formData.interest)}&body=${encodeURIComponent('Name: ' + formData.fullName + '\nEmail: ' + formData.email + '\nPhone: ' + formData.phone + '\n\nMessage:\n' + formData.message)}`} className="btn btn-primary">
                        Send via Email
                      </a>
                      <a href={`https://wa.me/919548354084?text=${encodeURIComponent('Hari Om! My name is ' + formData.fullName + '. I am interested in: ' + formData.interest + '. Message: ' + formData.message)}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                        Chat on WhatsApp
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSubmitStatus(null)}
                      className="cu-reset-btn"
                    >
                      ← Edit Enquiry Form
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="cu-form">
                  {/* Full Name */}
                  <div className="cu-field-group">
                    <label htmlFor="fullName" className="cu-label">
                      Full Name <span className="cu-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your full name"
                      className={`cu-input ${touched.fullName && errors.fullName ? 'cu-input-error' : ''}`}
                    />
                    {touched.fullName && errors.fullName && (
                      <span className="cu-error-text">{errors.fullName}</span>
                    )}
                  </div>

                  {/* Email & Phone Row */}
                  <div className="cu-field-row">
                    <div className="cu-field-group">
                      <label htmlFor="email" className="cu-label">
                        Email Address <span className="cu-required">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter your email address"
                        className={`cu-input ${touched.email && errors.email ? 'cu-input-error' : ''}`}
                      />
                      {touched.email && errors.email && (
                        <span className="cu-error-text">{errors.email}</span>
                      )}
                    </div>

                    <div className="cu-field-group">
                      <label htmlFor="phone" className="cu-label">
                        Mobile Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter your mobile number"
                        className={`cu-input ${touched.phone && errors.phone ? 'cu-input-error' : ''}`}
                      />
                      {touched.phone && errors.phone && (
                        <span className="cu-error-text">{errors.phone}</span>
                      )}
                    </div>
                  </div>

                  {/* I'm Interested In */}
                  <div className="cu-field-group">
                    <label htmlFor="interest" className="cu-label">
                      I'm Interested In
                    </label>
                    <select
                      id="interest"
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="cu-select"
                    >
                      <option value="General Enquiry">General Enquiry</option>
                      <option value="200 Hour Yoga Teacher Training">200 Hour Yoga Teacher Training</option>
                      <option value="300 Hour Yoga Teacher Training">300 Hour Yoga Teacher Training</option>
                      <option value="500 Hour Yoga Teacher Training">500 Hour Yoga Teacher Training</option>
                      <option value="95-Hour Kids Yoga Teacher Training">95-Hour Kids Yoga Teacher Training</option>
                      <option value="Face Yoga Teacher Training">Face Yoga Teacher Training</option>
                      <option value="Diet & Nutrition Course">Diet &amp; Nutrition Course</option>
                      <option value="PCOS & PCOD Management Program">PCOS &amp; PCOD Management Program</option>
                      <option value="Demo Class">Demo Class</option>
                      <option value="Enrollment / Payment Support">Enrollment / Payment Support</option>
                      <option value="Existing Student Support">Existing Student Support</option>
                      <option value="Teacher Application">Teacher Application</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="cu-field-group">
                    <label htmlFor="message" className="cu-label">
                      Message <span className="cu-required">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Tell us how we can help you"
                      className={`cu-textarea ${touched.message && errors.message ? 'cu-input-error' : ''}`}
                    />
                    {touched.message && errors.message && (
                      <span className="cu-error-text">{errors.message}</span>
                    )}
                  </div>

                  {/* Privacy Notice */}
                  <p className="cu-privacy-notice">
                    By submitting this form, you agree that PranaFlow Academy may contact you regarding your enquiry.
                    Please review our <Link to="/privacy-policy">Privacy Policy</Link> for information about how we handle submitted information.
                  </p>

                  {apiError && (
                    <div className="cu-error-text" style={{ marginBottom: '14px', fontSize: '13.5px' }}>
                      {apiError}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button type="submit" className="btn btn-primary cu-submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending Message...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Side Guidance Column */}
            <div className="cu-side-info">
              <div className="cu-side-box">
                <h3 className="cu-side-title">Direct Reach Options</h3>
                <p className="cu-side-desc">
                  Prefer a faster response? Our admissions team handles enquiries directly via Email and WhatsApp.
                </p>
                <div className="cu-side-links">
                  <a href="mailto:pranaflowacademy@gmail.com" className="cu-side-item">
                    <div className="cu-side-icon"><IconMail /></div>
                    <div>
                      <span className="cu-side-label">Email Us Directly</span>
                      <span className="cu-side-val">pranaflowacademy@gmail.com</span>
                    </div>
                  </a>
                  <a href="https://wa.me/919548354084" target="_blank" rel="noopener noreferrer" className="cu-side-item">
                    <div className="cu-side-icon cu-icon-whatsapp"><IconWhatsApp /></div>
                    <div>
                      <span className="cu-side-label">WhatsApp Live Chat</span>
                      <span className="cu-side-val">+91 95483 54084</span>
                    </div>
                  </a>
                  <a href="tel:09548354084" className="cu-side-item">
                    <div className="cu-side-icon"><IconPhone /></div>
                    <div>
                      <span className="cu-side-label">Phone Support</span>
                      <span className="cu-side-val">09548354084</span>
                    </div>
                  </a>
                </div>
              </div>

              <div className="cu-side-box" style={{ marginTop: '24px' }}>
                <h3 className="cu-side-title">Academy Hours</h3>
                <p className="cu-side-desc" style={{ marginBottom: '12px' }}>
                  Our admissions and student support team operates:
                </p>
                <ul className="cu-hours-list">
                  <li><span>Monday – Saturday:</span> <strong>9:00 AM – 7:00 PM IST</strong></li>
                  <li><span>Sunday:</span> <strong>10:00 AM – 4:00 PM IST</strong></li>
                </ul>
                <p className="cu-side-sub" style={{ marginTop: '12px' }}>
                  Online course materials and LMS dashboard remain accessible 24/7 for enrolled students.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────
           SECTION 4 — RISHIKESH LOCATION
          ────────────────────────────────────────────────────────────────────── */}
      <section className="cu-location section-padding" style={{ backgroundColor: 'var(--bg-surface-tint)' }}>
        <div className="container">
          <div className="cu-section-header">
            <span className="section-meta">OUR LOCATION</span>
            <h2 className="section-title cu-section-title">
              PranaFlow Academy in Rishikesh
            </h2>
          </div>

          <div className="cu-location-card">
            <div className="cu-location-badge">
              <IconMapPin />
            </div>
            <h3 className="cu-location-heading">
              Rooted in the Yoga Capital of the World
            </h3>
            <p className="cu-location-text">
              PranaFlow Academy is based in <strong>Rishikesh, Uttarakhand, India</strong> — a city internationally
              associated with yoga, meditation, spiritual study, and teacher education.
            </p>
            <p className="cu-location-text">
              Our online learning model enables students from different locations to access structured yoga and wellness
              education while remaining connected to an academy rooted in Rishikesh.
            </p>
            <div className="cu-location-details">
              <span className="badge" style={{ fontSize: '0.9rem' }}>
                Location: Rishikesh, Uttarakhand, India - 249201
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────
           SECTION 5 — WHAT CAN WE HELP WITH?
          ────────────────────────────────────────────────────────────────────── */}
      <section className="cu-help section-padding">
        <div className="container">
          <div className="cu-section-header">
            <span className="section-meta">HOW WE CAN HELP</span>
            <h2 className="section-title cu-section-title">
              Contact Us About
            </h2>
          </div>

          <div className="cu-help-grid">
            <div className="cu-help-card">
              <h3 className="cu-help-title">Course Guidance</h3>
              <p className="cu-help-desc">
                Help understanding which course best fits a student's current experience and learning goals.
              </p>
            </div>

            <div className="cu-help-card">
              <h3 className="cu-help-title">Batch &amp; Schedule Information</h3>
              <p className="cu-help-desc">
                Questions about upcoming batches, class schedules, course duration, and learning format.
              </p>
            </div>

            <div className="cu-help-card">
              <h3 className="cu-help-title">Fees &amp; Payment</h3>
              <p className="cu-help-desc">
                Questions regarding current course fees, installments, coupons, or enrollment payment options.
              </p>
            </div>

            <div className="cu-help-card">
              <h3 className="cu-help-title">Certification</h3>
              <p className="cu-help-desc">
                Clarification regarding the certificate associated with a particular program and relevant partner-school credentials.
              </p>
            </div>

            <div className="cu-help-card">
              <h3 className="cu-help-title">Demo Classes</h3>
              <p className="cu-help-desc">
                Questions about joining or registering for available live demo classes.
              </p>
            </div>

            <div className="cu-help-card">
              <h3 className="cu-help-title">Student Support</h3>
              <p className="cu-help-desc">
                Existing students can contact the academy regarding course-access or general academic-support questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────
           SECTION 6 — CERTIFICATION QUESTIONS TRUST BLOCK
          ────────────────────────────────────────────────────────────────────── */}
      <section className="cu-cert section-padding" style={{ backgroundColor: 'var(--bg-surface-tint)' }}>
        <div className="container cu-cert-inner">
          <span className="section-meta">TRANSPARENT CERTIFICATION</span>
          <h2 className="section-title cu-section-title" style={{ maxWidth: 720, margin: '12px auto 20px' }}>
            Questions About Certification?
          </h2>
          <p className="cu-cert-text">
            Certificate structures vary across PranaFlow Academy programs. Some Yoga Teacher Training pathways may involve appropriately registered Yoga Alliance partner schools, while specialized programs may carry PranaFlow Academy certificates or certificates of completion.
          </p>
          <p className="cu-cert-subtext">
            Please review the certification section on the individual course page or contact us if you need clarification before enrolling.
          </p>
          <div className="cu-cert-cta">
            <Link to="/courses" className="btn btn-primary">
              Explore Courses &amp; Details
            </Link>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────
           SECTION 7 — QUICK LINKS
          ────────────────────────────────────────────────────────────────────── */}
      <section className="cu-quick section-padding">
        <div className="container">
          <div className="cu-section-header">
            <span className="section-meta">FIND INFORMATION QUICKLY</span>
            <h2 className="section-title cu-section-title">
              You May Find Your Answer Here
            </h2>
          </div>

          <div className="cu-quick-grid">
            <Link to="/courses" className="cu-quick-card">
              <div className="cu-quick-title">Explore All Courses</div>
              <p className="cu-quick-desc">Compare 200h, 300h, 500h TTC, Face Yoga, Kids Yoga &amp; Nutrition.</p>
              <span className="cu-quick-arrow">→</span>
            </Link>

            <Link to="/about-us" className="cu-quick-card">
              <div className="cu-quick-title">About PranaFlow Academy</div>
              <p className="cu-quick-desc">Learn about our Rishikesh roots, teaching philosophy and values.</p>
              <span className="cu-quick-arrow">→</span>
            </Link>

            <Link to="/demo-class" className="cu-quick-card">
              <div className="cu-quick-title">Join a Demo Class</div>
              <p className="cu-quick-desc">Experience our live teaching format before enrolling.</p>
              <span className="cu-quick-arrow">→</span>
            </Link>

            <Link to="/teacher-apply" className="cu-quick-card">
              <div className="cu-quick-title">Apply to Teach</div>
              <p className="cu-quick-desc">Join PranaFlow Academy as a verified partner teacher.</p>
              <span className="cu-quick-arrow">→</span>
            </Link>

            <Link to="/privacy-policy" className="cu-quick-card">
              <div className="cu-quick-title">Privacy Policy</div>
              <p className="cu-quick-desc">Understand how we collect, store, and protect your data.</p>
              <span className="cu-quick-arrow">→</span>
            </Link>

            <Link to="/terms-and-conditions" className="cu-quick-card">
              <div className="cu-quick-title">Terms &amp; Conditions</div>
              <p className="cu-quick-desc">Review enrollment, fee policy, and academy terms.</p>
              <span className="cu-quick-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────
           SECTION 8 — FINAL CTA
          ────────────────────────────────────────────────────────────────────── */}
      <section className="cu-cta section-padding">
        <div className="container cu-cta-inner">
          <h2 className="cu-cta-heading">
            Ready to Explore Your Next Course?
          </h2>
          <p className="cu-cta-text">
            Browse PranaFlow Academy's yoga teacher training and wellness education programs to compare curriculum, duration, learning format, fees, and certification information.
          </p>
          <div className="cu-cta-btns">
            <Link to="/courses" className="btn btn-primary">
              Explore Our Courses
            </Link>
            <Link to="/demo-class" className="btn btn-secondary">
              Join a Demo Class
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ContactUs;
